import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'

interface ImportSkill {
  id: string; name: string; version: string; status: string; words: number; source: 'GitHub' | 'Drive' | 'Uploaded'
}

const MOCK_SKILLS: ImportSkill[] = [
  { id: '1', name: 'skillhub', version: 'v1.0', status: 'production', words: 127, source: 'GitHub' },
  { id: '2', name: 'mitel-ux', version: 'v2.4', status: 'production', words: 842, source: 'GitHub' },
  { id: '3', name: 'mitel-tone', version: 'v1.1', status: 'production', words: 334, source: 'GitHub' },
  { id: '4', name: 'campaign-specs', version: 'v0.3', status: 'staging', words: 215, source: 'GitHub' },
  { id: '5', name: 'mitel-cro', version: 'v2.1', status: 'production', words: 498, source: 'Drive' },
  { id: '6', name: 'content-localization', version: 'v0.1', status: 'staging', words: 98, source: 'Drive' },
  { id: '7', name: 'brand-guidelines', version: 'v1.0', status: 'staging', words: 312, source: 'Uploaded' },
]

const SOURCE_COLORS: Record<string, string> = {
  GitHub: 'bg-[#f0f0ef] text-[#555]',
  Drive: 'bg-[#e8f4fd] text-[#1565c0]',
  Uploaded: 'bg-[#f5f0ff] text-[#6d28d9]',
}

export default function OnboardingImportPage() {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<Set<string>>(new Set(MOCK_SKILLS.map(s => s.id)))

  const allChecked = selected.size === MOCK_SKILLS.length
  const toggleAll = () => {
    if (allChecked) setSelected(new Set())
    else setSelected(new Set(MOCK_SKILLS.map(s => s.id)))
  }
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <div className="min-h-screen bg-[#f5f5f4] dark:bg-[#111] flex items-center justify-center p-5">
      <div className="bg-white dark:bg-[#1e1e1e] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-xl p-9 w-full max-w-[580px]">
        <div className="mb-7"><Logo size="lg" /></div>
        <StepIndicator current={4} />

        <h1 className="text-[20px] font-medium tracking-[-0.3px] dark:text-[#f0f0ef] mb-1.5">Import existing skills</h1>
        <p className="text-[14px] text-[#888] dark:text-[#666] mb-7 leading-relaxed">
          We found {MOCK_SKILLS.length} skills across your connected sources. Select which ones to import.
        </p>

        <div className="flex items-center justify-between mb-2.5">
          <label className="flex items-center gap-1.5 text-[12px] text-[#555] dark:text-[#aaa] cursor-pointer">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} className="cursor-pointer" />
            Select all
          </label>
          <span className="text-[12px] text-[#888] dark:text-[#666]">
            {selected.size === MOCK_SKILLS.length ? `${MOCK_SKILLS.length} selected` : `${selected.size} of ${MOCK_SKILLS.length} selected`}
          </span>
        </div>

        {MOCK_SKILLS.map(skill => (
          <label key={skill.id} className="flex items-center gap-2.5 p-2.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[7px] mb-1.5 cursor-pointer hover:bg-[#fafaf9] dark:hover:bg-[#161616]">
            <input type="checkbox" checked={selected.has(skill.id)} onChange={() => toggle(skill.id)} className="flex-shrink-0 cursor-pointer" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-[#1a1a1a] dark:text-[#f0f0ef]">{skill.name}</div>
              <div className="text-[11px] text-[#aaa] dark:text-[#555] mt-0.5">{skill.version} · {skill.status} · {skill.words} words</div>
            </div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-[3px] font-medium flex-shrink-0 ${SOURCE_COLORS[skill.source]}`}>
              {skill.source}
            </span>
          </label>
        ))}

        <button
          onClick={() => navigate('/onboarding/success')}
          className="w-full mt-4 py-2.5 bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[7px] text-[14px] font-medium hover:bg-[#333] dark:hover:bg-[#ddd] cursor-pointer"
        >
          Import selected →
        </button>
        <button
          onClick={() => navigate('/onboarding/success')}
          className="w-full mt-2 py-2.5 bg-transparent border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[7px] text-[14px] font-medium hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer text-[#1a1a1a] dark:text-[#f0f0ef]"
        >
          Skip import
        </button>
      </div>
    </div>
  )
}

function StepIndicator({ current }: { current: number }) {
  const steps = ['Account', 'Sources', 'Destinations', 'Import']
  return (
    <div className="flex items-center justify-center mb-7">
      {steps.map((label, i) => {
        const n = i + 1
        const isDone = n < current
        const isCurrent = n === current
        return (
          <React.Fragment key={n}>
            {i > 0 && <div className="w-7 h-px bg-[#e5e5e3] dark:bg-[#2e2e2e] mx-1.5" />}
            <div className="flex items-center">
              <div className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${isDone || isCurrent ? 'bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a]' : 'bg-[#f0f0ef] dark:bg-[#252525] text-[#bbb] dark:text-[#444]'}`}>
                {isDone ? '✓' : n}
              </div>
              <span className={`text-[11px] ml-1.5 whitespace-nowrap ${isCurrent ? 'text-[#1a1a1a] dark:text-[#f0f0ef] font-medium' : 'text-[#bbb] dark:text-[#444]'}`}>{label}</span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}
